import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';

import {useProductOptions, MediaFile, Image} from '@shopify/hydrogen';
import {useCallback} from 'react';
import {useState} from 'react';
import {useEffect} from 'react';
import SimpleModal from './modal/SimpleModal';
import InnerImageZoom from 'react-inner-image-zoom';
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css';
import { isMobile } from '../lib/Utils';

/**
 * A client component that defines a media gallery for hosting images, 3D models, and videos of products
 */
export default function Gallery({product, sanityData}) {
  const media = product.media.nodes;

  const [viewportRef, embla] = useEmblaCarousel({
    containScroll: 'scrollSnap',
    slidesToScroll: 1,
    inViewThreshold: 1,
    skipSnaps: false,
    selectedClass: '',
    dragFree: true,
    align: 'start',
    loop: false,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [featureImage, setFeatureImage] = useState(null);
  const [galleryMedia, setGalleryMedia] = useState(media);
  const [activeIndex, setActiveIndex] = useState(null);
  const [openModalImage, setOpenModalImage] = useState(false);
  const [counter, setCounter] = useState(0);
  const [isMobileVar, setIsMobileVar] = useState(false);

  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla, setSelectedIndex]);

  const {selectedVariant} = useProductOptions();

  if (!media.length) {
    return null;
  }

  useEffect(()=>{
    if(isMobile()){ setIsMobileVar(true); }
  },[])

  useEffect(() => {
    if (!embla) return;
    onSelect();
    setScrollSnaps(embla.scrollSnapList());
    embla.on('select', onSelect);
  }, [embla, setScrollSnaps, onSelect]);

  useEffect(() => {
    const featuredMedia = selectedVariant.image || media[0]?.image;
    const featuredMediaSrc = featuredMedia?.url.split('?')[0];
    const galleryMedia = media.filter((med) => {
      if (
        med.mediaContentType === MODEL_3D_TYPE ||
        med.mediaContentType === VIDEO_TYPE ||
        med.mediaContentType === EXTERNAL_VIDEO_TYPE
      ) {
        return true;
      }

      return !med.image.url.includes(featuredMediaSrc);
    });

    setFeatureImage(featuredMedia);
    const gallery = [
      {
        mediaContentType: 'IMAGE',
        image: featuredMedia,
      },
      ...galleryMedia,
    ];
    setGalleryMedia(gallery);
  }, [selectedVariant, media]);

  const dataToUrl = (src, zoomSrc, width, height)=>{
    return encodeURIComponent(JSON.stringify({src, zoomSrc, width, height}));
  }

  return (
    <div tabIndex="-1" className="relative ">
      {sanityData.optionalTag && (
        <span className="single-product-tag"> {sanityData.optionalTag} </span>
      )}
      
      {featureImage && (
        <>
          <Image
            fetchpriority="high"
            data={featureImage}
            className="w-full mx-auto h-full md:h-auto object-cover object-center flex-shrink-0 md:flex-shrink-none snap-start md:col-span-2 cursor-pointer"
            alt={product.title || ''}
            onClick={()=>{ setOpenModalImage(true); }}
          />
            <SimpleModal openModal={openModalImage} closeModal={()=>{setOpenModalImage(false);}}>
            {isMobileVar ? (<>
              <iframe 
                  src={"/zoom-image?data="+dataToUrl(featureImage.url, featureImage.url, 300, 460)} 
                  className='zoomImageFrame' 
                  frameborder="0" 
                  scrolling="no"
                  width={300} 
                  height={460}>
              </iframe>
             </>) : (<> 
                <iframe 
                    src={"/zoom-image?data="+dataToUrl(featureImage.url, featureImage.url, 380, 540)} 
                    className='zoomImageFrame' 
                    frameborder="0" 
                    scrolling="no"
                    width={380} 
                    height={540}>
                </iframe>
             </>)}

            </SimpleModal>
        </>
      )}

      {galleryMedia && galleryMedia.length > 1 && (
        <div className="product-gallery-wrapper relative">
          <div
            className="product-gallery flex flex-wrap overflow-hidden"
            ref={viewportRef}
          >
            <div className="embla__container">
              {galleryMedia.map((med) => {
                let extraProps = {};
                if (med.mediaContentType === MODEL_3D_TYPE) {
                  extraProps = MODEL_3D_PROPS;
                }
                return (
                  <MediaFile
                    tabIndex="0"
                    key={med.id || med.image.id}
                    className={`product-thumb cursor-pointer ${
                      activeIndex === med.id ? 'active' : ''
                    }`}
                    data={med}
                    fetchpriority="low"
                    options={{
                      height: '485',
                      crop: 'center',
                    }}
                    onClick={() => {
                      setActiveIndex(med.id);
                      setFeatureImage(med.image);
                    }}
                    {...extraProps}
                  />
                );
              })}
            </div>
          </div>
          <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
          <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
        </div>
      )}
    </div>
  );
}

const MODEL_3D_TYPE = 'MODEL_3D';
const MODEL_3D_PROPS = {
  interactionPromptThreshold: '0',
};
const VIDEO_TYPE = 'VIDEO';
const EXTERNAL_VIDEO_TYPE = 'EXTERNAL_VIDEO';

const PrevButton = ({enabled, onClick}) => (
  <button
    className={` ${
      !enabled ? 'disabled' : 'fill-purple-120'
    } btn-prev xs:hidden left-0 absolute bottom-46 flex items-center justify-center rounded-full h-10 w-10 bg-white cursor-pointer`}
    onClick={onClick}
    disabled={!enabled}
  >
    <svg
      className={` ${!enabled ? 'fill-white' : 'fill-purple-120'}
          embla__button__svg h-5 w-5  fill-purple-120
        `}
      viewBox="137.718 -1.001 366.563 644"
    >
      <path d="M428.36 12.5c16.67-16.67 43.76-16.67 60.42 0 16.67 16.67 16.67 43.76 0 60.42L241.7 320c148.25 148.24 230.61 230.6 247.08 247.08 16.67 16.66 16.67 43.75 0 60.42-16.67 16.66-43.76 16.67-60.42 0-27.72-27.71-249.45-249.37-277.16-277.08a42.308 42.308 0 0 1-12.48-30.34c0-11.1 4.1-22.05 12.48-30.42C206.63 234.23 400.64 40.21 428.36 12.5z" />
    </svg>
  </button>
);

const NextButton = ({enabled, onClick}) => {
  return (
    <button
      className={` ${
        !enabled ? 'disabled' : 'fill-purple-120'
      } btn-next xs:hidden absolute right-0 bottom-46 flex items-center justify-center rounded-full h-10 w-10 bg-white cursor-pointer`}
      onClick={onClick}
      disabled={!enabled}
    >
      <svg
        className={`embla__button__svg  h-5 w-5 fill-purple-120  ${
          !enabled ? 'fill-white' : 'fill-purple-120'
        }`}
        viewBox="0 0 238.003 238.003"
      >
        <path d="M181.776 107.719L78.705 4.648c-6.198-6.198-16.273-6.198-22.47 0s-6.198 16.273 0 22.47l91.883 91.883-91.883 91.883c-6.198 6.198-6.198 16.273 0 22.47s16.273 6.198 22.47 0l103.071-103.039a15.741 15.741 0 0 0 4.64-11.283c0-4.13-1.526-8.199-4.64-11.313z" />
      </svg>
    </button>
  );
};
